import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Stack,
  Divider,
  TextField,
  ToggleButton,
  ToggleButtonGroup
} from '@mui/material';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';
import { membershipStatus as MEMBERSHIP_STATUS } from 'common/constants/options';
import couponApi from 'api/rest/coupon';
import PromoChip from 'components/shared/PromoChip';

const DEFAULT_PLANS = [];

// Acento dorado de selección en el toggle de facturación (monocromo, sin negro puro).
const billingToggleSx = {
  '& .MuiToggleButton-root': {
    color: 'text.secondary',
    borderColor: 'hairline.strong',
    fontWeight: 700,
    textTransform: 'none',
    py: 1
  },
  '& .Mui-selected': {
    color: 'gold.main !important',
    backgroundColor: 'surface.overlay !important'
  },
  '& .Mui-selected:hover': { backgroundColor: 'rgba(255,255,255,0.08) !important' }
};

// Fila de plan seleccionable (estilo premium): superficie off-black, borde dorado al
// seleccionar, precio tabular y check dorado. Sirve para plan principal (radio) y
// adicionales (multi) — el indicador es solo visual.
function PlanOptionRow({
  title,
  subtitle = null,
  price = null,
  originalPrice = null,
  benefitLabel = null,
  selected,
  onClick
}) {
  return (
    <Box
      role="button"
      aria-pressed={selected}
      onClick={onClick}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        px: 2,
        py: 1.75,
        borderRadius: 2,
        cursor: 'pointer',
        backgroundColor: selected ? 'gold.soft' : 'surface.overlay',
        border: '1px solid',
        borderColor: selected ? 'gold.main' : 'hairline.main',
        transition: 'background-color 200ms cubic-bezier(0.32,0.72,0,1), border-color 200ms cubic-bezier(0.32,0.72,0,1)',
        '&:hover': {
          borderColor: selected ? 'gold.main' : 'hairline.strong',
          backgroundColor: selected ? 'gold.soft' : 'action.hover'
        },
        '&:active': { transform: 'scale(0.99)' }
      }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ color: 'text.primary', fontWeight: 700 }} noWrap>
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: 'text.secondary' }}>
            {subtitle}
          </Typography>
        )}
      </Box>
      <Stack direction="row" alignItems="center" spacing={1.25} sx={{ flexShrink: 0 }}>
        <Stack alignItems="flex-end" spacing={0.25}>
          {originalPrice && (
            <Typography variant="caption" sx={{ color: 'text.secondary', textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>
              {originalPrice}
            </Typography>
          )}
          {price && (
            <Typography sx={{ color: selected ? 'gold.main' : 'text.primary', fontWeight: 800, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              {price}
            </Typography>
          )}
          {benefitLabel && (
            <Chip size="small" label={benefitLabel} sx={{ height: 18, maxWidth: 190, backgroundColor: 'gold.soft', color: 'var(--otx-gold-text)', fontWeight: 700, '& .MuiChip-label': { px: 0.75, fontSize: '0.6rem' } }} />
          )}
        </Stack>
        {selected ? (
          <CheckCircleIcon sx={{ color: 'gold.main' }} />
        ) : (
          <RadioButtonUncheckedIcon sx={{ color: 'text.disabled' }} />
        )}
      </Stack>
    </Box>
  );
}

PlanOptionRow.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  price: PropTypes.string,
  originalPrice: PropTypes.string,
  benefitLabel: PropTypes.string,
  selected: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired
};

const sectionLabelSx = {
  color: 'text.secondary',
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  fontSize: '0.7rem'
};

const listSx = { maxHeight: 420, overflowY: 'auto', pr: 0.5 };

/**
 * Modal premium para seleccionar planes antes de pagar (estilo OTRIX monocromo):
 * filas seleccionables con acento dorado en vez de dropdowns. Plan principal (uno) +
 * planes adicionales (0..N, solo en pago único) + toggle suscripción/pago único.
 */
function PlanSelectionModal({
  open,
  onClose,
  onConfirm,
  onSubscribe = () => {},
  plans = DEFAULT_PLANS,
  loading = false,
  currentPlanId = null,
  userCurrency = 'USD',
  userPlatform = 'otrix_app',
  gateway = 'wompi',
  gatewayEnabled = false,
  membershipStatus = null
}) {
  const { t } = useTranslation(['payments', 'labels', 'options']);
  const isGym = userPlatform === 'otrix_gym';

  let modalSubtitle = t('planSelect.modalInactive', { ns: 'payments', defaultValue: 'Activa tu plan o explora nuestros planes adicionales.' });
  if (membershipStatus === MEMBERSHIP_STATUS.active) {
    modalSubtitle = t('planSelect.modalActive', { ns: 'payments', defaultValue: 'Suma un plan adicional y potencia tu experiencia OTRIX.' });
  } else if (membershipStatus === MEMBERSHIP_STATUS.inArrears) {
    modalSubtitle = t('planSelect.modalArrears', { ns: 'payments', defaultValue: 'Ponte al día con tu plan o suma uno adicional.' });
  }

  // Solo mostrar planes con precio configurado para la moneda del usuario.
  const priceKey = userCurrency === 'USD' ? 'price_usd' : 'price_cop';

  const primaryPlans = useMemo(
    () => {
      const byType = isGym
        ? plans.filter((plan) => plan.type === 'presencial')
        : plans.filter((plan) => !plan.type || plan.type === 'primary');
      return byType.filter((plan) => plan[priceKey] > 0);
    },
    [plans, isGym, priceKey]
  );

  const addonPlans = useMemo(
    () => (isGym ? [] : plans.filter((plan) => plan.type === 'addon' && plan[priceKey] > 0)),
    [plans, isGym, priceKey]
  );

  const [selectedPrimaryPlan, setSelectedPrimaryPlan] = useState('');
  const [selectedAddonPlans, setSelectedAddonPlans] = useState([]);
  const [billingMode, setBillingMode] = useState('subscription');
  const [couponInput, setCouponInput] = useState('');
  const [coupon, setCoupon] = useState(null);
  const [couponError, setCouponError] = useState('');
  const [validatingCoupon, setValidatingCoupon] = useState(false);

  useEffect(() => {
    if (open && currentPlanId) {
      const planInList = primaryPlans.find((p) => p.id === String(currentPlanId));
      setSelectedPrimaryPlan(planInList ? planInList.id : '');
    }
  }, [open, currentPlanId, primaryPlans]);

  useEffect(() => {
    if (!open) {
      setSelectedPrimaryPlan('');
      setSelectedAddonPlans([]);
      setBillingMode('subscription');
      setCoupon(null);
      setCouponInput('');
      setCouponError('');
    }
  }, [open]);

  const currentPlan = useMemo(() => {
    if (!currentPlanId) return null;
    return (
      primaryPlans.find((p) => p.id === String(currentPlanId)) ||
      plans.find((p) => p.id === String(currentPlanId))
    );
  }, [currentPlanId, primaryPlans, plans]);

  // Planes principales SUSCRIBIBLES (flag de Wompi mensual). La pestaña "Suscripción mensual" SOLO
  // muestra estos; los adicionales siguen siendo exclusivos de "Pago único".
  const subscribablePrimaryPlans = useMemo(
    () => primaryPlans.filter((p) => p.is_subscribable),
    [primaryPlans]
  );

  // El toggle de suscripción aparece si HAY algún plan suscribible válido para la moneda/gateway.
  // Si el usuario YA está activo/suscrito, el modal es solo para sumar planes adicionales → sin toggle.
  const canShowSubscription = useMemo(() => {
    if (membershipStatus === MEMBERSHIP_STATUS.active) return false;
    if (subscribablePrimaryPlans.length === 0) return false;
    if (userCurrency === 'COP') return subscribablePrimaryPlans.some((p) => (p.price_cop || 0) > 0);
    return gateway === 'stripe' && gatewayEnabled; // USD
  }, [membershipStatus, subscribablePrimaryPlans, userCurrency, gateway, gatewayEnabled]);

  const subscriptionMode = canShowSubscription && billingMode === 'subscription';
  const displayedPrimaryPlans = subscriptionMode ? subscribablePrimaryPlans : primaryPlans;

  // Cupón: aplica al plan principal elegido (pago único y también suscripción mensual). El backend
  // RE-valida y aplica el descuento al plan que corresponda (fuente de verdad del monto cobrado).
  const couponPlanId = selectedPrimaryPlan || (currentPlanId ? String(currentPlanId) : (selectedAddonPlans[0] || null));

  useEffect(() => {
    // Si cambia el plan principal, el cupón validado ya no aplica: se limpia.
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  }, [selectedPrimaryPlan]);

  const applyCoupon = async () => {
    const code = couponInput.trim();
    if (!code || !couponPlanId) return;
    setValidatingCoupon(true);
    setCouponError('');
    try {
      const result = await couponApi.validateCoupon(code, couponPlanId);
      if (result.valid) {
        setCoupon(result);
      } else {
        setCoupon(null);
        setCouponError(result.reason || t('subscription.checkout.couponInvalid', { ns: 'payments', defaultValue: 'Cupón inválido.' }));
      }
    } catch {
      setCouponError(t('subscription.checkout.couponInvalid', { ns: 'payments', defaultValue: 'Cupón inválido.' }));
    } finally {
      setValidatingCoupon(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponInput('');
    setCouponError('');
  };

  // Fila "mantener plan actual": solo si el plan actual no está en la lista seleccionable.
  const showKeepCurrent = !!(currentPlan && !primaryPlans.find((p) => p.id === String(currentPlanId)));

  // Precio numérico defensivo (el backend a veces manda strings/null) → evita NaN y concatenaciones.
  const planPriceNum = (plan) => Number(plan?.[priceKey] ?? plan?.price_usd ?? 0) || 0;

  // Beneficio corporativo (auto por membresía) del plan. Tiene precedencia el cupón: si hay cupón,
  // no se muestra/aplica el beneficio org en el modal (el backend maneja la misma precedencia).
  const orgBenefitOf = (plan) => (plan?.org_benefit && plan.org_benefit.discount_percent > 0 ? plan.org_benefit : null);
  const planNetNum = (plan) => {
    const org = !coupon ? orgBenefitOf(plan) : null;
    return org ? (Number(org.discounted_price) || 0) : planPriceNum(plan);
  };
  const priceStr = (num) => `$${Number(num).toLocaleString()} ${userCurrency}`;
  // Props de precio para la fila: precio neto + original tachado + chip cuando hay beneficio org.
  const planRowProps = (plan) => {
    const org = !coupon ? orgBenefitOf(plan) : null;
    return {
      price: priceStr(org ? Number(org.discounted_price) : planPriceNum(plan)),
      originalPrice: org ? priceStr(planPriceNum(plan)) : null,
      benefitLabel: org ? `${org.organization_name} · -${org.discount_percent}%` : null
    };
  };

  const calculateTotal = () => {
    let total = 0;
    if (selectedPrimaryPlan) {
      const plan = primaryPlans.find((p) => p.id === selectedPrimaryPlan);
      if (plan) total += planNetNum(plan);
    } else if (currentPlan) {
      total += planNetNum(currentPlan);
    }
    selectedAddonPlans.forEach((planId) => {
      const plan = addonPlans.find((p) => p.id === planId);
      if (plan) total += planNetNum(plan);
    });
    return total;
  };

  // Total del beneficio org sobre los planes seleccionados (para la línea del resumen).
  const orgDiscountTotal = (() => {
    if (coupon) return 0;
    let d = 0;
    const acc = (plan) => {
      const org = plan ? orgBenefitOf(plan) : null;
      if (org) d += planPriceNum(plan) - Number(org.discounted_price);
    };
    if (selectedPrimaryPlan) acc(primaryPlans.find((p) => p.id === selectedPrimaryPlan));
    else if (currentPlan) acc(currentPlan);
    selectedAddonPlans.forEach((planId) => acc(addonPlans.find((p) => p.id === planId)));
    return Math.max(0, Math.round(d));
  })();

  const planSubtitle = (plan) =>
    plan.sessions > 0
      ? t('selectPlans.sessionsCount', {
          ns: 'payments',
          count: plan.sessions,
          defaultValue: `${plan.sessions} sesiones`
        })
      : null;

  const toggleAddon = (planId) =>
    setSelectedAddonPlans((prev) =>
      prev.includes(planId) ? prev.filter((id) => id !== planId) : [...prev, planId]
    );

  const handleConfirm = () => {
    let primaryId = null;
    if (selectedPrimaryPlan) primaryId = Number(selectedPrimaryPlan);
    else if (currentPlanId) primaryId = Number(currentPlanId);

    if (subscriptionMode && primaryId) {
      onSubscribe(primaryId, coupon ? coupon.code : null);
      return;
    }

    const selectedPlanIds = [];
    if (primaryId) selectedPlanIds.push(primaryId);
    selectedAddonPlans.forEach((planId) => selectedPlanIds.push(Number(planId)));
    if (selectedPlanIds.length > 0) onConfirm(selectedPlanIds, coupon ? coupon.code : null);
  };

  const totalPlansSelected =
    (selectedPrimaryPlan || (!selectedPrimaryPlan && currentPlanId) ? 1 : 0) + selectedAddonPlans.length;
  const total = calculateTotal();
  const couponDiscount = coupon ? Number(coupon.discount_amount || 0) : 0;
  const finalTotal = Math.max(0, total - couponDiscount);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: 'surface.content',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main',
          color: 'text.primary'
        }
      }}>
      <DialogTitle sx={{ textAlign: 'center', pt: 4, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="center" spacing={1}>
          <ShoppingCartIcon sx={{ color: 'gold.main' }} />
          <Typography variant="h5" fontWeight="bold" color="white">
            {t('selectPlans.title', { ns: 'payments' })}
          </Typography>
        </Stack>
        <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1, maxWidth: 420, mx: 'auto' }}>
          {modalSubtitle}
        </Typography>
      </DialogTitle>

      <DialogContent sx={{ pb: 4 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress sx={{ color: 'gold.main' }} />
          </Box>
        ) : (
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {canShowSubscription && (
              <ToggleButtonGroup
                value={billingMode}
                exclusive
                fullWidth
                onChange={(e, val) => {
                  if (!val) return;
                  setBillingMode(val);
                  if (val === 'subscription') {
                    setSelectedAddonPlans([]);
                    // En mensual solo hay suscribibles: si el plan elegido no lo es, se limpia.
                    setSelectedPrimaryPlan((cur) => (subscribablePrimaryPlans.some((p) => p.id === cur) ? cur : ''));
                  }
                }}
                sx={billingToggleSx}>
                <ToggleButton value="subscription">
                  {t('subscription.checkout.title', { ns: 'payments', defaultValue: 'Suscripción mensual' })}
                </ToggleButton>
                <ToggleButton value="one_time">
                  {t('selectPlans.oneTime', { ns: 'payments', defaultValue: 'Pago único' })}
                </ToggleButton>
              </ToggleButtonGroup>
            )}

            {/* Plan principal */}
            <Stack spacing={1}>
              <Typography sx={sectionLabelSx}>
                {isGym
                  ? t('selectPlans.presencialPlan', { ns: 'payments', defaultValue: 'Plan presencial' })
                  : t('selectPlans.primaryPlan', { ns: 'payments' })}
              </Typography>
              <Stack spacing={1} sx={listSx}>
                {showKeepCurrent && (
                  <PlanOptionRow
                    title={currentPlan.name}
                    subtitle={planSubtitle(currentPlan)}
                    {...planRowProps(currentPlan)}
                    selected={selectedPrimaryPlan === ''}
                    onClick={() => setSelectedPrimaryPlan('')}
                  />
                )}
                {displayedPrimaryPlans.map((plan) => (
                  <PlanOptionRow
                    key={plan.id}
                    title={plan.name}
                    subtitle={planSubtitle(plan)}
                    {...planRowProps(plan)}
                    selected={selectedPrimaryPlan === plan.id}
                    onClick={() => setSelectedPrimaryPlan(plan.id)}
                  />
                ))}
              </Stack>
            </Stack>

            {/* Planes adicionales (solo en pago único) */}
            {!subscriptionMode && addonPlans.length > 0 && (
              <Stack spacing={1}>
                <Typography sx={sectionLabelSx}>
                  {t('selectPlans.addonPlans', { ns: 'payments' })}
                </Typography>
                <Stack spacing={1} sx={listSx}>
                  {addonPlans.map((plan) => (
                    <PlanOptionRow
                      key={plan.id}
                      title={plan.name}
                      subtitle={planSubtitle(plan)}
                      {...planRowProps(plan)}
                      selected={selectedAddonPlans.includes(plan.id)}
                      onClick={() => toggleAddon(plan.id)}
                    />
                  ))}
                </Stack>
              </Stack>
            )}

            {/* Cupón (pago único y suscripción mensual). En suscripción se pasa al checkout. */}
            {totalPlansSelected > 0 && (
              <Stack spacing={1}>
                <Typography sx={sectionLabelSx}>
                  {t('selectPlans.coupon', { ns: 'payments', defaultValue: 'Cupón de descuento' })}
                </Typography>
                {coupon ? (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 1.5, py: 1, borderRadius: 2, backgroundColor: 'gold.soft', border: '1px solid', borderColor: 'gold.main' }}>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0 }}>
                      <PromoChip discountValue={coupon.discount_value} discountType={coupon.discount_type} />
                      <Typography variant="caption" sx={{ color: 'text.secondary', fontFamily: 'monospace' }} noWrap>{coupon.code}</Typography>
                    </Stack>
                    <Button variant="text" size="small" onClick={removeCoupon} sx={{ color: 'text.secondary', textTransform: 'none', flexShrink: 0 }}>
                      {t('subscription.checkout.removeCoupon', { ns: 'payments', defaultValue: 'Quitar' })}
                    </Button>
                  </Stack>
                ) : (
                  <Stack direction="row" spacing={1} alignItems="flex-start">
                    <TextField
                      value={couponInput}
                      onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                      placeholder={t('subscription.checkout.couponPlaceholder', { ns: 'payments', defaultValue: 'Código de cupón' })}
                      size="small"
                      fullWidth
                      error={Boolean(couponError)}
                      helperText={couponError || ' '}
                    />
                    <Button
                      onClick={applyCoupon}
                      disabled={!couponInput.trim() || validatingCoupon}
                      variant="outlined"
                      sx={{ mt: 0.25, py: 1, px: 2, borderRadius: 2, fontWeight: 700, textTransform: 'none', color: 'text.primary', borderColor: 'hairline.strong', whiteSpace: 'nowrap' }}>
                      {validatingCoupon ? <CircularProgress size={16} sx={{ color: 'gold.main' }} /> : t('subscription.checkout.applyCoupon', { ns: 'payments', defaultValue: 'Aplicar' })}
                    </Button>
                  </Stack>
                )}
              </Stack>
            )}

            {/* Resumen */}
            {totalPlansSelected > 0 && (
              <>
                <Divider sx={{ borderColor: 'hairline.main' }} />
                <Box
                  sx={{
                    bgcolor: 'surface.raised',
                    p: 2,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'hairline.main'
                  }}>
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {t('selectPlans.plansSelected', { ns: 'payments' })}
                      </Typography>
                      <Typography variant="body2" color="white">
                        {totalPlansSelected}
                      </Typography>
                    </Stack>
                    {couponDiscount > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('selectPlans.discount', { ns: 'payments', defaultValue: 'Descuento' })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>
                          -${couponDiscount.toLocaleString()} {userCurrency}
                        </Typography>
                      </Stack>
                    )}
                    {orgDiscountTotal > 0 && (
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          {t('selectPlans.orgBenefit', { ns: 'payments', defaultValue: 'Beneficio de tu organización' })}
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>
                          -${orgDiscountTotal.toLocaleString()} {userCurrency}
                        </Typography>
                      </Stack>
                    )}
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="subtitle1" fontWeight="bold" color="white">
                        {t('selectPlans.total', { ns: 'payments' })}
                      </Typography>
                      <Typography variant="subtitle1" fontWeight="bold" sx={{ color: 'gold.main', fontVariantNumeric: 'tabular-nums' }}>
                        ${finalTotal.toLocaleString()} {userCurrency}
                        {subscriptionMode && t('subscription.perMonthSuffix', { ns: 'payments', defaultValue: ' / mes' })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              </>
            )}

            {/* Botones */}
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button
                onClick={onClose}
                fullWidth
                variant="outlined"
                sx={{
                  py: 1.5,
                  borderRadius: 2.5,
                  fontWeight: 700,
                  textTransform: 'none',
                  color: 'text.primary',
                  borderColor: 'hairline.strong',
                  '&:hover': {
                    borderColor: 'hairline.strong',
                    background: 'surface.overlay'
                  }
                }}>
                {t('selectPlans.cancel', { ns: 'payments' })}
              </Button>
              <Button
                onClick={handleConfirm}
                fullWidth
                disabled={totalPlansSelected === 0 || loading}
                sx={premiumButtonSx}>
                {subscriptionMode
                  ? t('subscription.checkout.submit', { ns: 'payments', defaultValue: 'Suscribirme' })
                  : t('selectPlans.payWithWompi', { ns: 'payments', defaultValue: 'Pagar con Wompi' })}
              </Button>
            </Stack>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

PlanSelectionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onSubscribe: PropTypes.func,
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
      price_usd: PropTypes.number,
      price_cop: PropTypes.number,
      is_subscribable: PropTypes.bool,
      org_benefit: PropTypes.shape({
        discount_percent: PropTypes.number,
        base_price: PropTypes.number,
        discounted_price: PropTypes.number,
        currency: PropTypes.string,
        organization_name: PropTypes.string
      })
    })
  ),
  loading: PropTypes.bool,
  currentPlanId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  userCurrency: PropTypes.string,
  userPlatform: PropTypes.string,
  gateway: PropTypes.string,
  gatewayEnabled: PropTypes.bool,
  membershipStatus: PropTypes.string
};

export default PlanSelectionModal;
